import cn from 'classnames';
import { FC, HTMLProps, useRef } from 'react';
import { IoMdClose } from 'react-icons/io';
import { ArrowLeftIcon } from '../../../assets/svg/arrow-left';
import { MaskedInput } from '../../../components/input/masked-input';
import { useLang } from '../../../hooks/use-lang';
import { useToggle } from '../../../hooks/use-toggle';
import { Tooltip } from '../../tooltip/tooltip';
import s from './time-picker.module.css';

interface Props extends Omit<HTMLProps<HTMLDivElement>, 'onChange' | 'value'> {
  value?: string;
  onChange?: (time: string) => void;
  step?: number;
  minTime?: string;
}

export const TimePicker: FC<Props> = (props) => {
  const { t } = useLang();
  const {
    placeholder = t('чч : мм'),
    className,
    onChange,
    name,
    value = '',
    disabled,
    minTime,
    ...other
  } = props;
  const [open, , setOpen] = useToggle(false);

  const ref = useRef<HTMLDivElement>(null);

  const handleSelectHours = (time: string) => {
    const updated = value.split(':');
    updated[0] = time;
    onChange?.(updated.join(':'));
  };

  const handleSelectMinutes = (time: string) => {
    const updated = value.split(':');
    updated[1] = time;
    onChange?.(updated.join(':'));
  };

  const hours = Array.from({ length: 24 }).map((_, index) => {
    const formattedHours = index.toString().padStart(2, '0');
    return formattedHours;
  });

  const minutes = Array.from({ length: 60 }).map((_, index) => {
    const formattedMinutes = index.toString().padStart(2, '0');
    return formattedMinutes;
  });

  const trigger = value ? (
    <IoMdClose width={30} height={30} style={{ fontSize: 18 }} />
  ) : (
    <ArrowLeftIcon style={{ transform: open ? 'rotate(90deg)' : 'rotate(-90deg)', width: 12 }} />
  );

  return (
    <div className={cn(s.component, className)} ref={ref} {...other}>
      <MaskedInput
        placeholder={placeholder}
        onChange={onChange}
        value={value}
        name={name || 'time'}
        disabled={disabled}
        mask="__:__"
      />
      <Tooltip
        trigger={trigger}
        triggerWrapperClassName={s.time}
        wrapperClassName={s.timeWrapper}
        onClickTrigger={() => {
          if (value) {
            onChange?.('');
            setOpen(false);
          } else {
            setOpen((prev) => !prev);
          }
        }}
        disabled={disabled}
        open={open}
        fullScreen
        optionsWrapperClassName={s.pickers}
      >
        <ul className={s.list}>
          {hours.map((time) => {
            let disabled = false;
            const hour = value.split(':')[0];

            if (minTime) {
              const minHour = minTime.split(':')[0];
              const currentHour = time.split(':')[0];
              disabled = Number(currentHour) < Number(minHour);
            }

            return (
              <li
                key={time}
                onClick={() => handleSelectHours(time)}
                className={cn(s.item, time === hour && s.selected, disabled && s.disabled)}
              >
                {time}
              </li>
            );
          })}
        </ul>
        <ul className={s.list}>
          {minutes.map((time) => {
            let disabled = false;
            const minutes = value.split(':')[1];

            if (minTime) {
              const [minHour, minMin] = minTime.split(':');
              let minSumOfMinutes = Number(minHour) * 60 + Number(minMin);
              if (minHour === value.split(':')[0]) {
                minSumOfMinutes += 1;
              }
              const slotSumOfMinutes = Number(minHour) * 60 + Number(time);
              disabled = slotSumOfMinutes < minSumOfMinutes;
            }

            return (
              <li
                key={time}
                onClick={() => handleSelectMinutes(time)}
                className={cn(s.item, time === minutes && s.selected, disabled && s.disabled)}
              >
                {time}
              </li>
            );
          })}
        </ul>
      </Tooltip>
    </div>
  );
};
